import { useEffect, useRef, useState } from 'react';
import { transcribeOtoAudio } from '../services/api/oto';
import { formatApiError } from '../utils/apiError';

const MAX_BYTES = 8 * 1024 * 1024;

const release = session => {
  clearInterval(session.timer); clearTimeout(session.maximum); clearTimeout(session.watchdog);
  session.stream?.getTracks().forEach(track => track.stop());
};
const discard = session => {
  session.discarded = true; session.request?.abort(); release(session);
  try { if (['recording', 'paused'].includes(session.recorder?.state)) session.recorder.stop(); } catch { /* already stopped */ }
  session.chunks = [];
};

export const useOtoDictation = () => {
  const [state, setState] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const current = useRef(null);
  const mounted = useRef(true);
  const isCurrent = session => mounted.current && current.current === session && !session.discarded;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; if (current.current) discard(current.current); current.current = null; };
  }, []);
  const fail = (session, message) => {
    const active = isCurrent(session);
    discard(session);
    if (active) { current.current = null; setError(message); setState('idle'); }
  };
  const finish = async session => {
    if (session.finalized) return;
    session.finalized = true; release(session);
    if (!isCurrent(session)) { session.chunks = []; return; }
    const blob = new Blob(session.chunks, { type: session.mime }); session.chunks = [];
    if (!blob.size) return fail(session, 'Aucun son enregistré. Recommencez ou utilisez le clavier.');
    if (blob.size > MAX_BYTES) return fail(session, 'Enregistrement trop volumineux. Dictez une demande plus courte.');
    setState('transcribing');
    session.request = new AbortController();
    try {
      const file = new File([blob], session.mime.includes('mp4') ? 'dictee.mp4' : 'dictee.webm', { type: session.mime });
      const response = await transcribeOtoAudio(file, session.request.signal);
      if (isCurrent(session)) { setTranscript(response.data.text); setState('review'); current.current = null; }
    } catch (err) {
      if (isCurrent(session)) fail(session, formatApiError(err, 'Transcription indisponible. Vous pouvez saisir votre demande.'));
    } finally { release(session); }
  };
  const stopSession = session => {
    if (!isCurrent(session) || session.finalized || session.stopping) return;
    session.stopping = true;
    clearInterval(session.timer); clearTimeout(session.maximum);
    setState('stopping');
    // Le clavier ne doit pas rester bloqué si le navigateur n'émet jamais onstop.
    session.watchdog = setTimeout(() => {
      if (isCurrent(session) && !session.finalized) fail(session, 'L’arrêt du micro n’a pas abouti. Le clavier reste disponible.');
    }, 3000);
    try {
      if (['recording', 'paused'].includes(session.recorder?.state)) session.recorder.stop();
      else fail(session, 'L’enregistrement est déjà arrêté. Vous pouvez recommencer.');
    } catch { fail(session, 'Impossible de terminer la dictée. Vous pouvez utiliser le clavier.'); }
  };
  const start = async () => {
    if (current.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return setError('La dictée nécessite un navigateur compatible et une connexion sécurisée. Le clavier reste disponible.');
    const session = { discarded: false, finalized: false, chunks: [], bytes: 0 }; current.current = session;
    setError(''); setTranscript(''); setSeconds(0); setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isCurrent(session)) { stream.getTracks().forEach(track => track.stop()); return; }
      session.stream = stream;
      session.mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find(type => MediaRecorder.isTypeSupported(type));
      if (!session.mime) return fail(session, 'Format d’enregistrement non pris en charge par ce navigateur.');
      session.recorder = new MediaRecorder(stream, { mimeType: session.mime });
      session.recorder.ondataavailable = event => {
        if (!isCurrent(session) || !event.data.size) return;
        session.chunks.push(event.data); session.bytes += event.data.size;
        if (session.bytes > MAX_BYTES) stopSession(session);
      };
      session.recorder.onerror = () => fail(session, 'L’enregistrement a été interrompu. Le clavier reste disponible.');
      session.recorder.onstop = () => finish(session);
      session.recorder.start(1000); setState('recording');
      session.timer = setInterval(() => { if (isCurrent(session)) setSeconds(value => value + 1); }, 1000);
      session.maximum = setTimeout(() => stopSession(session), 90000);
    } catch (err) {
      fail(session, err.name === 'NotAllowedError' ? 'Accès au microphone refusé. Autorisez-le dans le navigateur ou utilisez le clavier.' : 'Microphone indisponible. Le clavier reste disponible.');
    }
  };
  const cancel = () => {
    if (current.current) discard(current.current);
    current.current = null; setTranscript(''); setState('idle'); setError('');
  };
  const accept = () => { const text = transcript.trim(); setTranscript(''); setState('idle'); return text; };
  return { state, seconds, transcript, setTranscript, error, start, stop: () => current.current && stopSession(current.current), cancel, accept };
};